import type { SKU, MetricRow } from './types';

export interface CoverageCell {
  suite: string;
  navigation: string;
  count: number;
  products: Set<string>;
}

export const NAVIGATIONS = [
  'Behavior',
  'Compliance',
  'Historical',
  'Monitoring',
  'Operational',
  'Strategic',
] as const;

export function buildCoverageMatrix(metrics: MetricRow[]): {
  suites: string[];
  navigations: string[];
  cells: Map<string, CoverageCell>;
} {
  const suites = Array.from(
    new Set(metrics.map((m) => m.VieSuite).filter((s) => s && !/done|hecho|pendiente|revisar|ok/i.test(s))),
  ).sort();

  const cells = new Map<string, CoverageCell>();
  for (const m of metrics) {
    const suite = m.VieSuite;
    const nav = m.DashboardType;
    if (!suite || !nav) continue;
    const key = `${suite}::${nav}`;
    let c = cells.get(key);
    if (!c) {
      c = { suite, navigation: nav, count: 0, products: new Set() };
      cells.set(key, c);
    }
    c.count++;
    if (m.Product) c.products.add(m.Product);
  }

  const allNavs = Array.from(new Set(metrics.map((m) => m.DashboardType).filter(Boolean))).sort();

  return { suites, navigations: allNavs, cells };
}

export interface TreeNode {
  id: string;
  label: string;
  type: 'platform' | 'suite' | 'product' | 'module' | 'navigation' | 'kpi';
  count?: number;
  meta?: Record<string, string>;
  children?: TreeNode[];
}

export function buildTree(metrics: MetricRow[], skus: SKU[]): TreeNode {
  const root: TreeNode = {
    id: 'root',
    label: 'Pulse',
    type: 'platform',
    children: [],
  };

  const suiteMap = new Map<string, TreeNode>();
  for (const m of metrics) {
    if (!m.VieSuite || !m.Product) continue;
    if (/done|hecho|pendiente|revisar|ok/i.test(m.VieSuite)) continue;

    let suiteNode = suiteMap.get(m.VieSuite);
    if (!suiteNode) {
      suiteNode = { id: `suite::${m.VieSuite}`, label: m.VieSuite, type: 'suite', children: [], count: 0 };
      suiteMap.set(m.VieSuite, suiteNode);
      root.children!.push(suiteNode);
    }

    let productNode = suiteNode.children!.find((c) => c.label === m.Product);
    if (!productNode) {
      productNode = { id: `prod::${m.VieSuite}::${m.Product}`, label: m.Product, type: 'product', children: [], count: 0 };
      suiteNode.children!.push(productNode);
    }

    let moduleNode = productNode.children!.find((c) => c.label === (m.Module || '(sin módulo)'));
    if (!moduleNode) {
      moduleNode = {
        id: `mod::${m.VieSuite}::${m.Product}::${m.Module}`,
        label: m.Module || '(sin módulo)',
        type: 'module',
        children: [],
        count: 0,
      };
      productNode.children!.push(moduleNode);
    }

    let navNode = moduleNode.children!.find((c) => c.label === (m.DashboardType || '(sin tipo)'));
    if (!navNode) {
      navNode = {
        id: `nav::${m.VieSuite}::${m.Product}::${m.Module}::${m.DashboardType}`,
        label: m.DashboardType || '(sin tipo)',
        type: 'navigation',
        children: [],
        count: 0,
      };
      moduleNode.children!.push(navNode);
    }

    const kpiLabel = m.KPIDescription || '(KPI sin descripción)';
    navNode.children!.push({
      id: `kpi::${navNode.id}::${navNode.children!.length}`,
      label: kpiLabel,
      type: 'kpi',
      meta: {
        Owner: m.OwnerCargo,
        Ficha: m.FichaTecnica,
        Accion: m.Accion,
      },
    });

    navNode.count = (navNode.count || 0) + 1;
    moduleNode.count = (moduleNode.count || 0) + 1;
    productNode.count = (productNode.count || 0) + 1;
    suiteNode.count = (suiteNode.count || 0) + 1;
  }

  for (const s of root.children!) {
    s.children!.sort((a, b) => a.label.localeCompare(b.label));
    for (const p of s.children!) {
      p.children!.sort((a, b) => a.label.localeCompare(b.label));
    }
  }
  root.children!.sort((a, b) => a.label.localeCompare(b.label));

  return root;
}

export interface Summary {
  totalSKUs: number;
  skusListoDesarrollo: number;
  skusListaOferta: number;
  totalMetrics: number;
  metricsWithKPI: number;
  metricsWithOwner: number;
  uniqueSuitesPortfolio: number;
  uniqueSuitesMetrics: number;
  uniqueProducts: number;
  uniqueModules: number;
  byCategory: Record<string, number>;
  byDashboardType: Record<string, number>;
  byProduct: Record<string, number>;
  bySuite: Record<string, number>;
}

export function summarize(skus: SKU[], metrics: MetricRow[]): Summary {
  const byCategory: Record<string, number> = {};
  const byDashboardType: Record<string, number> = {};
  const byProduct: Record<string, number> = {};
  const bySuite: Record<string, number> = {};
  for (const m of metrics) {
    if (m.Category) byCategory[m.Category] = (byCategory[m.Category] || 0) + 1;
    if (m.DashboardType) byDashboardType[m.DashboardType] = (byDashboardType[m.DashboardType] || 0) + 1;
    if (m.Product) byProduct[m.Product] = (byProduct[m.Product] || 0) + 1;
    if (m.VieSuite && !/done|hecho|pendiente|revisar|ok/i.test(m.VieSuite)) {
      bySuite[m.VieSuite] = (bySuite[m.VieSuite] || 0) + 1;
    }
  }
  return {
    totalSKUs: skus.length,
    skusListoDesarrollo: skus.filter((s) => s.ListoDesarrollo).length,
    skusListaOferta: skus.filter((s) => s.ListaOferta).length,
    totalMetrics: metrics.length,
    metricsWithKPI: metrics.filter((m) => m.KPIDescription).length,
    metricsWithOwner: metrics.filter((m) => m.OwnerCargo).length,
    uniqueSuitesPortfolio: new Set(skus.map((s) => s.Suite).filter(Boolean)).size,
    uniqueSuitesMetrics: Object.keys(bySuite).length,
    uniqueProducts: new Set(metrics.map((m) => m.Product).filter(Boolean)).size,
    uniqueModules: new Set(metrics.map((m) => `${m.Product}::${m.Module}`).filter(Boolean)).size,
    byCategory,
    byDashboardType,
    byProduct,
    bySuite,
  };
}
